import classNames from 'classnames'
import { t } from '../../../utils'
import {
  labCommunityPostPath,
  labCommunityTopicPath,
  labEventsPath,
  labTrainingPath
} from '../../../utils/routeHelpers'
import styles from './_welcomeModal.module.scss'
import { useRouter } from 'next/router'
import { useState, useContext } from 'react'
import Image from 'next/image'
import tickImg from '@public/images/tick.png'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import TourVideo from './TourVideo'
import {
  CommunityPost,
  LabWelcomeGuideStepEnum
} from '../../../__generated__/graphql'
import { useMutation, useQuery } from '@apollo/client'
import {
  COMMUNITY_TOP_QUESTION_QUERY,
  COMPLETE_WELCOME_GUIDE_STEP_MUTATION
} from '../../../gql'
import {
  LabOnboardingContext,
  PENDING_CONGRATS_AFTER_NEXT_NAV,
  PENDING_CONGRATS_STORAGE_KEY
} from './LabOnboardingProvider'

const WELCOME_TOUR_WISTIA_ID = 'v52ijwdhmv'
const COMMUNITY_TOUR_TOPIC_ID = 8
const INTRODUCE_YOURSELF_TOPIC_ID = 9

interface VideoProps {
  videoTitle: string
  wistiaId: string
}
interface WelcomeStepOptionsType {
  stepName: LabWelcomeGuideStepEnum
  title: string
  description?: string
  url?: string
  videoOptions?: VideoProps
}
interface WelcomeModalProps {
  closeModal: () => void
}

const StepItem: React.FC<{
  step: WelcomeStepOptionsType
  stepNumber: number
  isCompleted: boolean
  onClick: () => void
}> = ({ step, stepNumber, isCompleted, onClick }) => {
  return (
    <>
      <li onClick={onClick}>
        <div className={styles.stepNumber}>{stepNumber}</div>
        <div>
          <div className={styles.stepTitle}>{step.title}</div>
          {step.description && (
            <div className={styles.stepDescription}>{step.description}</div>
          )}
        </div>
        <div
          className={classNames(styles.stepStatus, {
            [styles.stepStatusCompleted]: isCompleted
          })}
        >
          {isCompleted && (
            <Image
              src={tickImg}
              width={33}
              height={27}
              alt={t('step completed')}
            />
          )}
        </div>
      </li>
    </>
  )
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({ closeModal }) => {
  const { data: topQuestionData } = useQuery(COMMUNITY_TOP_QUESTION_QUERY)

  const topQuestionPost = topQuestionData?.communityTopQuestion as CommunityPost

  const getOrientedSteps: WelcomeStepOptionsType[] = [
    {
      stepName: LabWelcomeGuideStepEnum.WelcomeTour,
      title: t('Watch Your 5-Minute Orientation'),
      description: t('See everything inside LAB+ in one quick overview.'),
      videoOptions: {
        videoTitle: t('Welcome to LAB+')!,
        wistiaId: WELCOME_TOUR_WISTIA_ID
      }
    },
    {
      stepName: LabWelcomeGuideStepEnum.CommunityTour,
      title: t('Learn How the Community Works'),
      description: t('Understand how to post and engage confidently.'),
      url: labCommunityTopicPath(COMMUNITY_TOUR_TOPIC_ID)
    },
    {
      stepName: LabWelcomeGuideStepEnum.IntroduceYourself,
      title: t('Say Hello to the Community'),
      description: t('Introduce yourself and start building connection.'),
      url: labCommunityTopicPath(INTRODUCE_YOURSELF_TOPIC_ID)
    }
  ]

  const activateGrowthSteps: WelcomeStepOptionsType[] = [
    {
      stepName: LabWelcomeGuideStepEnum.TopQuestion,
      title: t("Jump Into This Week's Featured Question"),
      description: t('Join the conversation instantly.'),
      url: topQuestionPost ? labCommunityPostPath(topQuestionPost) : ''
    },
    {
      stepName: LabWelcomeGuideStepEnum.Training,
      title: t('Explore the Training Library'),
      description: t('Find a course that fits your next step.'),
      url: labTrainingPath()
    },
    {
      stepName: LabWelcomeGuideStepEnum.Event,
      title: t('Reserve Your Spot at a Live Coaching Session'),
      description: t('Lock in your first live win.'),
      url: labEventsPath()
    }
  ]

  const router = useRouter()

  const { userSteps, completedCount, totalSteps, loading } =
    useContext(LabOnboardingContext)

  const [isVideoOpen, setIsVideoOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState<WelcomeStepOptionsType>()
  const [showCongratsAfterVideo, setShowCongratsAfterVideo] = useState(false)

  const [completeStep] = useMutation(COMPLETE_WELCOME_GUIDE_STEP_MUTATION)

  if (loading) return null

  const deferCongratsModal = () => {
    window.sessionStorage.setItem(PENDING_CONGRATS_STORAGE_KEY, 'true')
    closeModal()
  }

  const closeVideo = () => {
    setIsVideoOpen(false)

    if (showCongratsAfterVideo) {
      setShowCongratsAfterVideo(false)
      deferCongratsModal()
    }
  }

  const onStepClick = async (
    stepOption: WelcomeStepOptionsType,
    isCompleted: boolean
  ) => {
    const isVideoStep = !!stepOption.videoOptions
    const isUrlStep = !!stepOption.url

    const willCompleteAllSteps =
      !isCompleted && completedCount + 1 >= totalSteps

    if (!isCompleted) {
      await completeStep({
        variables: { stepTitle: stepOption.stepName }
      })
    }

    if (isUrlStep) {
      if (willCompleteAllSteps) {
        window.sessionStorage.setItem(
          PENDING_CONGRATS_STORAGE_KEY,
          PENDING_CONGRATS_AFTER_NEXT_NAV
        )
        await router.push(stepOption.url)
        return
      }

      return router.push(stepOption.url)
    }

    if (willCompleteAllSteps) {
      if (!isVideoStep) {
        deferCongratsModal()
        return
      }
    }

    setCurrentStep(stepOption)

    if (isVideoStep) {
      if (willCompleteAllSteps) {
        setShowCongratsAfterVideo(true)
        setIsVideoOpen(true)
        return
      }

      setIsVideoOpen((open) => !open)
      return
    }

    setIsVideoOpen(false)
  }

  return (
    <>
      {isVideoOpen && (
        <div className={styles.welcomeVideoWrapper}>
          <TourVideo {...currentStep.videoOptions} close={closeVideo} />
        </div>
      )}
      <div
        className={classNames(styles.welcomeContainer, {
          [styles.welcomeContainerOpen]: isVideoOpen
        })}
      >
        <header>
          <div>
            <h1>{t('Start Strong. Build Momentum.')}</h1>
            <p>{t('Get started with LAB+ and unlock all it has to offer.')}</p>
          </div>
          <button
            className={styles.closeButton}
            type="button"
            onClick={closeModal}
          >
            <FontAwesomeIcon
              icon={faTimes}
              color="#fff"
              className={styles.closeIcon}
            />
          </button>
        </header>

        <div className={styles.stepsContent}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              {t('Get Oriented')}{' '}
              <span className={styles.sectionSubtitle}>
                ({t('10 Minutes')})
              </span>
            </h2>
            <ul>
              {getOrientedSteps.map((stepOption, index) => {
                const isCompleted = !!userSteps.find(
                  (step) => step.stepTitle === stepOption.stepName
                )?.completedAt
                return (
                  <StepItem
                    key={stepOption.stepName}
                    step={stepOption}
                    stepNumber={index + 1}
                    isCompleted={isCompleted}
                    onClick={() => onStepClick(stepOption, isCompleted)}
                  />
                )
              })}
            </ul>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>{t('Activate Your Growth')}</h2>
            <ul>
              {activateGrowthSteps.map((stepOption, index) => {
                const isCompleted = !!userSteps.find(
                  (step) => step.stepTitle === stepOption.stepName
                )?.completedAt
                return (
                  <StepItem
                    key={stepOption.stepName}
                    step={stepOption}
                    stepNumber={getOrientedSteps.length + index + 1}
                    isCompleted={isCompleted}
                    onClick={() => onStepClick(stepOption, isCompleted)}
                  />
                )
              })}
            </ul>
          </section>
        </div>
      </div>
    </>
  )
}

export default WelcomeModal
